import {
  Text,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { InputField } from 'app/components/Core';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { dynamicYupResolver } from 'app/utils/utils';
import { PlaylistAudio } from 'types/playlists.types';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getUpdatePlaylistAudioLink } from 'app/store/Playlists/selectors';
import { useFetchValidationSchema } from 'app/utils/hooks';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import { formErrorMessages } from 'app/components/Playlist/RenameDialog/constants';
import { updatePlaylistAudioPropsEffect } from 'app/store/Playlists/effects';

interface RenameDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseComplete: () => void;
  playlistAudio: PlaylistAudio | null;
}

export const RenameDialog = ({
  isOpen,
  playlistAudio,
  onClose,
  onCloseComplete,
}: RenameDialogProps) => {
  const dispatch = useAppDispatch();
  const updatePlaylistAudioLink = useAppSelector(getUpdatePlaylistAudioLink);

  const defaultAudioPropsValues = {
    title: playlistAudio?.title || playlistAudio?.audio.title || '',
  };

  const { validationSchema } = useFetchValidationSchema({
    link: updatePlaylistAudioLink,
  });

  const yupValidationSchema = useMemo(
    () =>
      validationSchema &&
      transformSchemaToYup(
        validationSchema,
        defaultAudioPropsValues,
        formErrorMessages
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationSchema]
  );

  const {
    handleSubmit,
    register,
    reset,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = useForm<{ title: string }>({
    defaultValues: defaultAudioPropsValues,
    mode: 'all',
    context: {
      yupValidationSchema,
    },
    resolver: dynamicYupResolver,
  });

  const handleClose = () => {
    onClose();
    reset();
  };

  const onSubmit = async ({ title }) => {
    try {
      if (playlistAudio) {
        await dispatch(
          updatePlaylistAudioPropsEffect({
            playlistAudioGuid: playlistAudio.guid,
            title,
          })
        ).unwrap();
      }
      handleClose();
    } catch (e) {
      // Continue
    }
  };

  useEffect(() => {
    reset({ title: playlistAudio?.title || playlistAudio?.audio.title || '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistAudio, reset]);

  const titleFieldProps = register('title', {
    setValueAs: (value) => value.trim(),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onCloseComplete={onCloseComplete}
      data-testid="note-dialog"
    >
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader data-testid="dialog-title">
            Rename
            <Text
              color="textSubdued"
              fontSize="xs"
              overflow="hidden"
              textOverflow="ellipsis"
              title={playlistAudio?.title}
            >
              {playlistAudio?.title || playlistAudio?.audio.title}
            </Text>
          </ModalHeader>
          <ModalBody data-testid="dialog-content">
            <Text mb={6} fontSize="xs">
              Renaming the Audio file here will only affect the title shown in
              this Playlist.
            </Text>
            <InputField
              {...titleFieldProps}
              label="Title"
              isInvalid={Boolean(errors.title)}
              errorMessage={errors.title?.message}
              helperText={`Source title: ${playlistAudio?.audio.title}`}
              data-testid="AudioTitleInPlaylist"
            />
          </ModalBody>
          <ModalFooter data-testid="dialog-actions">
            <Button
              variant="secondary"
              data-testid="cancel-button"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              isDisabled={isSubmitting || !isValid || !isDirty}
              data-testid="rename-button"
              type="submit"
              ml={2}
            >
              Rename
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
